import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtom, useAtomValue, useStore } from "jotai";
import { ErrorState, Icon, Spinner } from "@common/index";
import { useOptionApi } from "@/hooks/apiHooks/useOptionApi";
import { useOptionValueApi } from "@/hooks/apiHooks/useOptionValueApi";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import {
    optionsAtom,
    isFetchingOptionsAtom,
    isProcessingOptionsAtom,
} from "@/store/optionAtoms";
import { isProcessingOptionValuesAtom } from "@/store/optionValueAtoms";
import { isManageOptionsOpenAtom } from "@/store/manageOptionsAtoms";
import type { OptionResponse, OptionValueDto } from "@models/option";
import { activeOptionValues } from "../_helpers/activeOptionValues";
import { OptionList } from "./_components/OptionList";
import { OptionValuePanel } from "./_components/OptionValuePanel";

type PendingDelete =
    | { kind: "option"; id: number }
    | { kind: "value"; id: number }
    | null;

export const ManageOptionsModal = () => {
    const [isOpen, setIsOpen] = useAtom(isManageOptionsOpenAtom);
    const options = useAtomValue(optionsAtom);
    const isFetching = useAtomValue(isFetchingOptionsAtom);
    const isProcessingOptions = useAtomValue(isProcessingOptionsAtom);
    const isProcessingValues = useAtomValue(isProcessingOptionValuesAtom);

    const { getOptions, createOption, updateOption, deleteOption } =
        useOptionApi();
    const { createOptionValue, deleteOptionValue } = useOptionValueApi();

    // User's explicit selection. Falls back to the first option at render time
    // when null or when the selected option no longer exists (e.g. deleted).
    const [requestedOptionId, setRequestedOptionId] = useState<number | null>(
        null,
    );
    const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
    // Whether the last list read failed. Only shown while there is no list to
    // show — a failed read must not look like "no options exist".
    const [loadFailed, setLoadFailed] = useState(false);
    // Which open / Retry read is the current one. Each read takes the next
    // number and a close moves it on, so an older read can tell it is stale.
    const loadTicket = useRef(0);
    const store = useStore();

    const panelRef = useRef<HTMLDivElement>(null);

    // Derive the active option (and its values) from the store rather than
    // mirroring it into local state — keeps a single source of truth and avoids
    // setState-in-effect. Re-fetching options after a value mutation refreshes
    // the nested `values` here.
    const selectedOption = useMemo<OptionResponse | null>(() => {
        if (options.length === 0) return null;
        return (
            options.find((o) => o.id === requestedOptionId) ?? options[0]
        );
    }, [options, requestedOptionId]);

    const selectedOptionId = selectedOption?.id ?? null;
    // A deleted value comes back from the re-read flagged inactive: hide it.
    const values: OptionValueDto[] = activeOptionValues(
        selectedOption?.values ?? [],
    );

    const clearPendingDelete = useCallback(() => {
        setPendingDelete(null);
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        clearPendingDelete();
        // The next open is a fresh attempt: the flag goes, and a read still
        // in flight is retired so it cannot settle into that next open.
        loadTicket.current += 1;
        setLoadFailed(false);
    }, [setIsOpen, clearPendingDelete]);

    // The flag is the modal's own failed state, written by the CURRENT read
    // only — true when it fails, false when it succeeds. A read that a close
    // or a newer read has retired writes nothing.
    // One failure, one alert: with no list held the failed state below is the
    // message, so the toast is skipped; with a list held that list stays on
    // screen and the toast is the only thing that reports the failure. Read
    // from the store when the read is issued, so this callback stays stable
    // and the open effect does not re-run when the list arrives.
    const loadOptions = useCallback(() => {
        const ticket = ++loadTicket.current;
        const isCurrent = () => ticket === loadTicket.current;

        getOptions({
            skipErrorNotification: store.get(optionsAtom).length === 0,
        }).then(
            () => {
                if (isCurrent()) setLoadFailed(false);
            },
            () => {
                if (isCurrent()) setLoadFailed(true);
            },
        );
    }, [getOptions, store]);

    const retryOptions = () => {
        setLoadFailed(false);
        loadOptions();
    };

    // Fetch options each time the modal opens.
    useEffect(() => {
        if (!isOpen) return;
        loadOptions();
    }, [isOpen, loadOptions]);

    // Focus management + scroll lock + Esc + Tab trap. Project overlay pattern.
    // isProcessing stays false here: Escape and the backdrop close this modal
    // even mid-request. The trap still holds when an Add or Delete button
    // goes `loading` (disabled) or a confirm row unmounts under focus. The
    // backdrop below is a sibling of the panel, so a drag out of the panel
    // never clicks it: its plain onClick stays.
    const { panelProps } = useDialogFocusTrap({
        panelRef,
        isOpen,
        isProcessing: false,
        onRequestClose: handleClose,
    });

    const handleSelect = useCallback(
        (optionId: number) => {
            setRequestedOptionId(optionId);
            clearPendingDelete();
        },
        [clearPendingDelete],
    );

    const handleCreateOption = useCallback(
        (name: string) => {
            createOption({ name })
                .then(() => getOptions())
                .catch(() => {
                    /* surfaced by notification layer */
                });
        },
        [createOption, getOptions],
    );

    const handleRenameOption = useCallback(
        (optionId: number, name: string) => {
            updateOption(optionId, { name })
                .then(() => getOptions())
                .catch(() => {
                    /* surfaced by notification layer */
                });
        },
        [updateOption, getOptions],
    );

    const handleCreateValue = useCallback(
        (value: string) => {
            if (selectedOptionId === null) return;
            createOptionValue(selectedOptionId, { value })
                .then(() => getOptions())
                .catch(() => {
                    /* surfaced by notification layer */
                });
        },
        [createOptionValue, selectedOptionId, getOptions],
    );

    const handleRequestDeleteOption = useCallback(
        (option: OptionResponse) => {
            setPendingDelete({ kind: "option", id: option.id });
        },
        [],
    );

    const handleRequestDeleteValue = useCallback((value: OptionValueDto) => {
        setPendingDelete({ kind: "value", id: value.id });
    }, []);

    // A delete is a soft delete with no "in use" refusal (AC-10, DecisionLog
    // 2026-10-13): a failure of any kind is reported by the API layer's toast
    // and closes the confirmation.
    const handleConfirmDelete = useCallback(() => {
        if (!pendingDelete) return;

        if (pendingDelete.kind === "option") {
            deleteOption(pendingDelete.id)
                .then(() => {
                    // Clear the selection pointing at the now-deleted option.
                    setRequestedOptionId(null);
                    clearPendingDelete();
                })
                .catch(() => clearPendingDelete());
            return;
        }

        if (selectedOptionId === null) return;
        const valueId = pendingDelete.id;
        deleteOptionValue(selectedOptionId, valueId)
            .then(() => getOptions())
            .then(() => clearPendingDelete())
            .catch(() => clearPendingDelete());
    }, [
        pendingDelete,
        deleteOption,
        deleteOptionValue,
        selectedOptionId,
        getOptions,
        clearPendingDelete,
    ]);

    if (!isOpen) return null;

    const isProcessing = isProcessingOptions || isProcessingValues;

    return (
        <>
            <div
                data-testid="manage-options-backdrop"
                onClick={handleClose}
                className="fixed inset-0 z-40 bg-black/30"
            />
            <div
                ref={panelRef}
                data-testid="manage-options-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Manage Options"
                {...panelProps}
                className="fixed left-1/2 top-1/2 z-50 flex max-h-[80vh] w-[48rem] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg bg-base-100 text-neutral shadow-xl focus:outline-none"
            >
                <div className="flex items-center justify-between border-b border-base-200 p-4">
                    <h2 className="text-lg font-bold">Manage Options</h2>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={handleClose}
                        className="btn btn-ghost btn-sm btn-square text-neutral"
                    >
                        <Icon name="close" className="text-2xl" />
                    </button>
                </div>

                {isFetching && options.length === 0 ? (
                    <div className="p-8">
                        <Spinner size="lg" />
                    </div>
                ) : loadFailed && options.length === 0 ? (
                    <ErrorState
                        message="Couldn't load options."
                        onRetry={retryOptions}
                    />
                ) : (
                    <div className="grid flex-1 grid-cols-2 gap-4 overflow-hidden p-4">
                        <div className="overflow-hidden border-r border-base-200 pr-4">
                            <OptionList
                                options={options}
                                selectedOptionId={selectedOptionId}
                                isProcessing={isProcessing}
                                pendingDeleteId={
                                    pendingDelete?.kind === "option"
                                        ? pendingDelete.id
                                        : null
                                }
                                onSelect={handleSelect}
                                onCreate={handleCreateOption}
                                onRename={handleRenameOption}
                                onRequestDelete={handleRequestDeleteOption}
                                onConfirmDelete={handleConfirmDelete}
                                onCancelDelete={clearPendingDelete}
                            />
                        </div>
                        <div className="overflow-hidden">
                            {selectedOption ? (
                                <OptionValuePanel
                                    values={values}
                                    isProcessing={isProcessing}
                                    pendingDeleteId={
                                        pendingDelete?.kind === "value"
                                            ? pendingDelete.id
                                            : null
                                    }
                                    onCreate={handleCreateValue}
                                    onRequestDelete={handleRequestDeleteValue}
                                    onConfirmDelete={handleConfirmDelete}
                                    onCancelDelete={clearPendingDelete}
                                />
                            ) : (
                                <p className="text-sm text-neutral">
                                    Select an option to manage its values.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
